import { compose, bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { useQuery, useMutation } from '@apollo/client';
import React, { useState, useMemo } from 'react';

/* eslint-disable import/no-unresolved */
import useStore from 'playbook-web/store';

import { captureException } from '@playbookux/playbook-web-core';
import {
  first,
} from 'lodash';
import {
  func, shape, string,
} from 'prop-types';
import ModalDelete from '../delete-modal';
import { fetchCompany, setCurrentCompany } from '../../../../core/actions';

import {
  ContainerList,
  Container,
  DangerButton,
  DangerMsg,
  Divider,
  ItemList,
  Link,
  Section,
  TextList,
  Title,
} from './styles';

import {
  DELETE_COMPANY,
  GET_COMPANY_INVITES,
  GET_CURRENT_COMPANY_MEMBER,
} from '../graphql';
import {
  HELP_CENTER_LINK,
} from './constants';
import FormUpdateProfile from './components/form-update-profile';
import InfoCollectionSection from './components/info-collection-section';
import FormUpdateWorkspace from './components/form-update-workspace';

const General = (props) => {
  const {
    company = {},
    meId = '',
    onFetchCompany = () => {},
    onSetCurrentCompany = () => {},
    history: { push } = {},
  } = props;

  const setCompany = useStore((state) => state.setCompany);

  const [showModalDelete, setShowModalDelete] = useState(false);

  const companyId = company?.id || '';

  const { data: { companyMembers } = {} } = useQuery(GET_COMPANY_INVITES, {
    variables: {
      companyId,
      status: [],
    },
    skip: !companyId,
  });

  const { data: { companyMembers: currentMember } = {} } = useQuery(GET_CURRENT_COMPANY_MEMBER, {
    variables: {
      userId: meId,
      companyId,
    },
    skip: !meId || !companyId,
  });

  const currentUser = useMemo(() => first(currentMember), [currentMember]);

  const [deleteError, setDeleteError] = useState('');

  const updatedCurrentCompany = async () => {
    const newCompany = await onFetchCompany();
    onSetCurrentCompany(newCompany);
    // add to mf store as OBJECT and not companyId
    setCompany(newCompany);
    if (!newCompany) {
      push('/create-workspace');
      return;
    }
    push('/');
  };

  const [deleteCompany, { loading: mutationLoading }] = useMutation(DELETE_COMPANY);

  const handleDeleteCompany = async () => {
    try {
      await deleteCompany({
        variables: { companyId },
      });
      setShowModalDelete(false);
      await updatedCurrentCompany();
    } catch (e) {
      captureException('DELETE_COMPANY_FAILED', e);
      setDeleteError('Error while deleting company');
    }
  };

  const closeDeleteModal = () => {
    setShowModalDelete(false);
    setDeleteError('');
  };

  return (
    <>
      <Section>
        <Title>Workspace Name</Title>
        <Container>
          <FormUpdateWorkspace
            onFetchCompanies={onFetchCompany}
            currentCompany={company}
            currentCompanyId={companyId}
          />
        </Container>
      </Section>

      <Divider />

      {(currentUser) && (
        <FormUpdateProfile currentUser={currentUser} meId={meId} />
      )}

      {(currentUser && company) && (
        <InfoCollectionSection
          currentCompany={company}
          onFetchCompanies={() => onFetchCompany(companyId)}
        />
      )}

      {(currentUser && (currentUser.role === 'admin')) && (
        <Container>
          <DangerMsg>Danger Zone: Delete workspace</DangerMsg>
          <ContainerList bulleted>
            <ItemList>
              <TextList>Remove all team members, then click ‘Delete workspace’</TextList>
            </ItemList>
            <ItemList>
              <TextList>You will no longer have access to the projects in this workspace</TextList>
            </ItemList>
            <ItemList>
              <TextList>
                {'This is permanent and cannot be undone. '}
                <Link
                  href={HELP_CENTER_LINK}
                  target="_blank"
                >
                  Learn more
                </Link>
              </TextList>
            </ItemList>
          </ContainerList>
          <DangerButton
            disabled={companyMembers && companyMembers.length > 1}
            onClick={() => setShowModalDelete(true)}
          >
            Delete Workspace
          </DangerButton>
          {showModalDelete && (
            <ModalDelete
              onConfirm={handleDeleteCompany}
              title="Delete account?"
              confirmText="Yes, delete my account"
              cancelText="Cancel, keep my account"
              show={showModalDelete}
              onClose={closeDeleteModal}
              errorMessage={deleteError}
              loading={mutationLoading}
            />
          )}
        </Container>
      )}
    </>
  );
};

General.propTypes = {
  company: shape.isRequired,
  history: shape({
    push: func,
  }).isRequired,
  meId: string.isRequired,
  onFetchCompany: func.isRequired,
  onSetCurrentCompany: func.isRequired,
};

const mapDispatchToProps = (dispatch) => bindActionCreators({
  onSetCurrentCompany: setCurrentCompany,
  onFetchCompany: fetchCompany,
}, dispatch);

const mapStateToProps = ({ authentication }) => ({
  company: authentication.company,
  meId: authentication.profile.user_id,
});

const enhance = compose(
  connect(mapStateToProps, mapDispatchToProps),
);

export default enhance(General);
